import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBookOpen, faBell, faChartLine, faMotorcycle, faCircleQuestion } from "@fortawesome/free-solid-svg-icons"
import Navbar from "../../components/LandPage/Navbar"
import { useCourierViewContext } from "../../context/useCourierViewContext"
import { ECourierView } from "../../Enum/ECourierView"
import { CourierPanelPage } from "./CourierPanelPage"
import { CourierRoutePlanPage } from "./CourierRoutePlanPage"
import { CourierRegisterPage } from "./CourierSignPage"
import { CourierNavbar } from "./Components/CourierNavbar"
import { CourierHelpCenterPage } from "./CourierHelpCenterPage"
import { SEO } from "../../seo/Seo"
import { useOrganizationSchema } from "../../seo/schema"
import { ECourierStatusType } from "../../Enum/ECourierStatusType"
import { useCourierAccount } from "../../hooks/Courier/useCourierAccount"
import { useSaveCourier } from "../../hooks/Courier/useSaveCourier"
import { deliveryMemory } from "../../Objects/Memory/Memory"
import { DeliveryEntity } from "../../Objects/DeliveryEntity/DeliveryEntity"
import { CourierFinancialPage } from "./CourierFinancialPage"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { CourierTutorialsPage } from "./CourierTutorialsPage"
import TutorialShowcase from "../../components/LandPage/TutorialShowcase"
import { useGetDelivery } from "../../hooks/Delivery/useGetDelivery"
import { useDeliveryHub } from "../../hooks/Delivery/useDeliveryHub"
import { useCourierOfferNotifications } from "../../hooks/Delivery/useCourierOfferNotifications"
import { PushNotificationsPage } from "../Notifications/PushNotificationsPage"

export const CourierPage = () => {
  const { auth, currentView, setCurrentView } = useCourierViewContext()

  const organizationSchema = useOrganizationSchema()

  const [activeTab, setActiveTab] = useState<"panel" | "help" | "financial" | "tutorials" | "notifications">("panel")

  const [isRoutePlanOpen, setIsRoutePlanOpen] = useState(false)

  const [isOnline, setIsOnline] = useState(false)

  const [isStatusUpdating, setIsStatusUpdating] = useState(false)

  const [courierId, setCourierId] = useState<string>()

  const [externalOfferCount, setExternalOfferCount] = useState(0)

  const [latestExternalOffer, setLatestExternalOffer] = useState<DeliveryEntity | null>(null)

  const { t } = useTranslation("courierHelp")

  const { handleGetCourierProfile } = useCourierAccount()

  const { handleUpdateStatusAsync } = useSaveCourier()

  const { handleGetDelivery } = useGetDelivery()

  const { notifyNewDelivery } = useCourierOfferNotifications({
    activeTab,
    isOnline,
    onExternalOffer: (delivery) => {
      setExternalOfferCount((current) => current + 1)
      setLatestExternalOffer(delivery)
    },
  })

  const handleIncomingDelivery = useCallback(
    (delivery: DeliveryEntity) => {
      const alreadyExists = deliveryMemory.listData.some((item) => item.id === delivery.id)

      if (!alreadyExists) {
        notifyNewDelivery(delivery)
      }
    },
    [notifyNewDelivery],
  )

  const deliveries = useDeliveryHub(courierId, isOnline, handleIncomingDelivery)

  const latestExternalOfferLabel = useMemo(() => {
    if (!latestExternalOffer) {
      return ""
    }

    const customerName = latestExternalOffer.order?.customerEntity?.personEntity?.name?.trim()

    const orderNumber =
      latestExternalOffer.orderId > 0 ? t("order_label", { id: latestExternalOffer.orderId }) : t("new_order")

    return customerName ? `${orderNumber} • ${customerName}` : orderNumber
  }, [latestExternalOffer, t])

  const panelTabLabel = useMemo(() => {
    if (externalOfferCount <= 0) {
      return t("label_tab_panel")
    }

    return `${t("label_tab_panel")} (${externalOfferCount})`
  }, [externalOfferCount, t])

  const courierTabs = useMemo(
    () => [
      {
        key: "panel" as const,
        label: panelTabLabel,
        icon: faMotorcycle,
        onClick: () => {
          setActiveTab("panel")
          setExternalOfferCount(0)
          setLatestExternalOffer(null)
        },
        badge: externalOfferCount > 0 ? externalOfferCount : null,
      },
      {
        key: "help" as const,
        label: t("label_tab_help"),
        icon: faCircleQuestion,
        onClick: () => setActiveTab("help"),
      },
      {
        key: "financial" as const,
        label: t("label_tab_financial"),
        icon: faChartLine,
        onClick: () => setActiveTab("financial"),
      },
      {
        key: "tutorials" as const,
        label: t("label_tab_tutorials"),
        icon: faBookOpen,
        onClick: () => setActiveTab("tutorials"),
      },
      {
        key: "notifications" as const,
        label: t("label_tab_notifications"),
        icon: faBell,
        onClick: () => setActiveTab("notifications"),
      },
    ],
    [externalOfferCount, panelTabLabel, t],
  )

  useEffect(() => {
    if (auth.status) {
      setCurrentView(ECourierView.SignInView)
    } else {
      setCurrentView(ECourierView.RegisterView)
    }
  }, [auth])

  useEffect(() => {
    if (!auth.status || currentView !== ECourierView.SignInView) {
      return
    }

    handleGetCourierProfile((courier) => {
      setIsOnline(courier.status === ECourierStatusType.Online)

      setCourierId(courier.id)
    })
  }, [auth.status, currentView])

  useEffect(() => {
    if (!courierId) {
      return
    }

    if (isOnline) {
      handleGetDelivery(() => {})
      return
    }

    deliveryMemory.setList([])
  }, [courierId, isOnline])

  const sendLocationHeartbeat = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      handleUpdateStatusAsync(
        ECourierStatusType.Online,
        position.coords.latitude,
        position.coords.longitude,
        (status: boolean) => {
          if (!status) {
            setIsOnline(false)
          }
        },
        { silent: true },
      )
    })
  }

  useEffect(() => {
    if (!isOnline) {
      return
    }

    sendLocationHeartbeat()

    const interval = window.setInterval(() => {
      sendLocationHeartbeat()
    }, 1000 * 60)

    return () => window.clearInterval(interval)
  }, [isOnline])

  const handleOnlineToggle = () => {
    if (isStatusUpdating) {
      return
    }

    const newStatus = !isOnline
    setIsStatusUpdating(true)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleUpdateStatusAsync(
          newStatus ? ECourierStatusType.Online : ECourierStatusType.Offline,
          position.coords.latitude,
          position.coords.longitude,
          (status: boolean) => {
            setIsOnline(status)

            if (!status || !newStatus) {
              deliveryMemory.setList([])
              setExternalOfferCount(0)
              setLatestExternalOffer(null)
            }

            setIsStatusUpdating(false)
          },
        )
      },
      () => {
        if (!newStatus) {
          handleUpdateStatusAsync(ECourierStatusType.Offline, undefined, undefined, (status: boolean) => {
            setIsOnline(status)
            deliveryMemory.setList([])
            setExternalOfferCount(0)
            setLatestExternalOffer(null)
            setIsStatusUpdating(false)
          })
        } else {
          setIsStatusUpdating(false)
        }
      },
    )
  }

  return (
    <div className="min-h-screen bg-white pb-24 font-sans ">
      <SEO
        title={t("seo_title")}
        description={t("seo_description")}
        path="/courier"
        structuredData={organizationSchema}
      />

      {auth.status ? (
        <CourierNavbar
          isOnline={isOnline}
          onToggleOnline={handleOnlineToggle}
          isStatusUpdating={isStatusUpdating}
          activeDeliveriesCount={deliveries.length}
        />
      ) : (
        <Navbar showFunctions={false} />
      )}

      <main className="mx-auto max-w-7xl px-1 py-2">
        {currentView === ECourierView.RegisterView && <CourierRegisterPage />}

        {currentView === ECourierView.SignInView && (
          <>
            <Animate type={AnimationTypes.slideRightIn}>
              <section className="sticky top-[76px] z-10 mb-3 space-y-1 bg-white/95 py-1 backdrop-blur md:top-[84px]">
                <div className="overflow-x-auto pb-1">
                  <div className="flex min-w-max items-center gap-1 rounded-lg bg-white p-1.5 shadow-sm">
                    {courierTabs.map((tab) => {
                      const isActive = activeTab === tab.key

                      return (
                        <button
                          key={tab.key}
                          onClick={tab.onClick}
                          className={`relative flex items-center gap-2 rounded-lg border px-3.5 py-2.5 text-sm font-bold transition-all ${
                            isActive
                              ? "border-transparent bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                          }`}
                          data-interaction-description-key="interactionDescriptions:pages.delivery.courierPage.button.tabOnClick"
                        >
                          <FontAwesomeIcon
                            icon={tab.icon}
                            className={`text-sm ${isActive ? "text-white" : "text-orange-500"}`}
                          />

                          <span className="whitespace-nowrap">{tab.label}</span>

                          {tab.badge && (
                            <span
                              className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-black ${
                                isActive ? "bg-white/25 text-white" : "bg-slate-800 text-white"
                              }`}
                            >
                              {tab.badge}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </section>
            </Animate>

            {externalOfferCount > 0 && activeTab !== "panel" && (
              <Animate type={AnimationTypes.fadeInTraslate}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("panel")
                    setExternalOfferCount(0)
                    setLatestExternalOffer(null)
                  }}
                  className="mb-4 flex w-full animate-pulse items-start justify-between gap-3 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-100 via-orange-100 to-red-100 px-4 py-3 text-left shadow-md ring-2 ring-amber-200/70"
                  data-interaction-description-key="interactionDescriptions:pages.delivery.courierPage.button.newOfferTitle"
                >
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.2em] text-orange-700">
                      {t("new_offer_title")}
                    </div>

                    <div className="mt-1 text-sm font-extrabold text-slate-900">
                      {latestExternalOfferLabel || t("new_offer_fallback", { count: externalOfferCount })}
                    </div>

                    <div className="mt-1 text-xs font-semibold text-orange-800">{t("new_offer_description")}</div>
                  </div>

                  <span className="rounded-full bg-red-600 px-3 py-1 text-xs font-black text-white shadow">
                    {externalOfferCount}
                  </span>
                </button>
              </Animate>
            )}

            {activeTab === "panel" && (
              <CourierPanelPage deliveries={deliveries} onOpenRoutePlan={() => setIsRoutePlanOpen(true)} />
            )}

            {activeTab === "help" && <CourierHelpCenterPage />}

            {activeTab === "financial" && <CourierFinancialPage />}

            {activeTab === "tutorials" && <CourierTutorialsPage />}

            {activeTab === "notifications" && <PushNotificationsPage />}
          </>
        )}

        {!auth.status && <TutorialShowcase />}
      </main>

      {isRoutePlanOpen && <CourierRoutePlanPage deliveries={deliveries} onClose={() => setIsRoutePlanOpen(false)} />}
    </div>
  )
}
